import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";
import { IMask, IMaskInput } from "react-imask";

import type { CalendarProps, Mode } from "@/components/ui/calendar";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import SvgCalendar from "@/icons/Solid/Calendar";
import { cn } from "@/lib/utils";

import { Button } from "./button";
import type { InputProps } from "./input";

type CalendarValue<TMode extends Mode> =
  | (TMode extends "single"
      ? Date
      : TMode extends "multiple"
        ? Array<Date>
        : TMode extends "range"
          ? DateRange
          : never)
  | undefined;

export type DatePickerProps<
  TMode extends Mode = "single",
  TCalendar extends CalendarProps<TMode> = CalendarProps<TMode>,
> = Omit<
  TCalendar,
  "className" | "disabled" | "onSelect" | "selected" | "onChange" | "mode"
> & {
  mode?: TMode;
  className?: string | React.ClassAttributes<HTMLDivElement>;
  disabled?: boolean;
  calendarDisabled?: TCalendar["disabled"];
  onChange: (value: CalendarValue<TMode>) => void;
  value?: CalendarValue<TMode>;
  placeholder?: InputProps["placeholder"];
};

const DEFAULT_INPUT_FORMAT = "DD/MM/YYYY";

export const getInputValue = <TMode extends Mode>(
  selected: CalendarProps<TMode>["selected"],
  mode: TMode,
  format: string = DEFAULT_INPUT_FORMAT
) => {
  if (!selected) return "";
  if (mode === "single") return dayjs(selected as Date).format(format);
  if (mode === "multiple")
    return (selected as Array<Date>)
      .map((d) => dayjs(d as Date).format(format))
      .join(", ");
  if (mode === "range") {
    const { from, to } = selected as DateRange;
    return `${from ? dayjs(from).format(format) : "DD/MM/YYYY"}-${to ? dayjs(to).format(format) : "DD/MM/YYYY"}`;
  }
};

export const getMonth = <TMode extends Mode>(
  selected: CalendarProps<TMode>["selected"],
  mode: TMode
): Date => {
  if (!selected) return new Date();
  if (mode === "single") return selected as Date;
  if (mode === "multiple") return (selected as Array<Date>)[0];
  if (mode === "range") {
    const { from } = selected as DateRange;
    return from ?? new Date();
  }
  return new Date();
};

type DateInputProps = {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
} & Omit<
  React.ComponentProps<typeof IMaskInput>,
  "value" | "onAccept" | "mask"
>;

const DateInput = ({
  value,
  onChange,
  className,
  ...props
}: DateInputProps) => {
  const [inputValue, setInputValue] = useState(
    value && dayjs(value).format("DD/MM/YYYY")
  );
  const patternMask = useMemo(
    () =>
      IMask.createMask({
        mask: "00/00/0000",
        lazy: false,
        overwrite: true,
      }),
    []
  );
  const isSyncing = useRef(false);
  const toDayKey = (d?: Date) =>
    d ? dayjs(d).format("YYYY-MM-DD") : undefined;
  const lastEmittedDayKey = useRef<string | undefined>(toDayKey(value));

  const onInputAccept = (nextValue: string) => {
    if (isSyncing.current) return;
    setInputValue(nextValue);

    // Count digits only (works with masked placeholders/literals)
    const digits = nextValue.replace(/\D/g, "");

    // Nothing typed -> clear date
    if (digits.length === 0) {
      if (lastEmittedDayKey.current !== undefined) {
        lastEmittedDayKey.current = undefined;
        onChange(undefined);
      }
      return;
    }

    if (digits.length < 8) {
      return;
    }

    const parsed = dayjs(nextValue, "DD/MM/YYYY", true);
    if (!parsed.isValid()) return;

    const nextDayKey = parsed.format("YYYY-MM-DD");
    if (nextDayKey === lastEmittedDayKey.current) return;
    lastEmittedDayKey.current = nextDayKey;
    onChange(parsed.startOf("day").toDate());
  };

  useEffect(() => {
    const next = value ? dayjs(value).format("DD/MM/YYYY") : "";
    isSyncing.current = true;
    setInputValue(next);
    lastEmittedDayKey.current = toDayKey(value);
    queueMicrotask(() => {
      isSyncing.current = false;
    });
  }, [value]);

  return (
    <IMaskInput
      mask={patternMask}
      placeholder="jj/mm/aaaa"
      overwrite
      lazy={false}
      value={inputValue ?? ""}
      onAccept={(val) => onInputAccept(String(val))}
      className={cn(
        "min-w-[110px] flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-50",
        className
      )}
      disabled={props.disabled}
      {...props}
    />
  );
};

export const DatePicker = <TMode extends Mode = "single">({
  className,
  disabled,
  value,
  mode = "single" as TMode,
  calendarDisabled,
  onChange,
  ...props
}: DatePickerProps<TMode>) => {
  const [month, setMonth] = useState(getMonth(value, mode));

  const handleChange = (selectedValue: CalendarValue<TMode>) => {
    if (onChange) onChange(selectedValue);
    setMonth(getMonth(selectedValue, mode));
  };

  const handleRangeSelect = (selectedRange: DateRange) => {
    if (selectedRange.to) {
      return onChange({
        from: selectedRange.from,
        to: dayjs(selectedRange.to).endOf("day").toDate(),
      } as CalendarValue<TMode>);
    }
    onChange(value as CalendarValue<TMode>);
  };

  return (
    <div className={cn("relative text-gray-600 w-full", className)}>
      {mode === "single" && (
        <DateInput
          value={value as Date}
          onChange={(selectedValue) =>
            handleChange(selectedValue as CalendarValue<TMode>)
          }
          disabled={disabled}
          className="text-black"
        />
      )}
      {mode === "range" && (
        <div className="flex gap-2 h-10 w-full rounded-md border border-input bg-background pr-10">
          <DateInput
            value={(value as DateRange)?.from}
            onChange={(fromValue) =>
              handleChange(
                (fromValue ?? (value as DateRange)?.to)
                  ? ({
                      ...value,
                      from: fromValue,
                    } as CalendarValue<TMode>)
                  : undefined
              )
            }
            className="border-none focus:border focus-visible:ring-offset-0 bg-transparent h-full"
            disabled={disabled}
          />
          <span className="self-center">-</span>
          <DateInput
            value={(value as DateRange)?.to}
            onChange={(toValue) =>
              handleChange(
                (toValue ?? (value as DateRange)?.from)
                  ? ({
                      ...value,
                      to: dayjs(toValue as Date)
                        .endOf("day")
                        .toDate(),
                    } as CalendarValue<TMode>)
                  : undefined
              )
            }
            className="border-none focus:border focus-visible:ring-offset-0 bg-transparent h-full"
            disabled={disabled}
          />
        </div>
      )}
      <div className="absolute right-0 top-0">
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button size="icon" variant="ghost" disabled={disabled}>
              <SvgCalendar width={20} height={20} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            {/* @ts-expect-error having difficulties to make ts work here */}
            <Calendar
              mode={mode}
              month={month}
              selected={value}
              disabled={calendarDisabled}
              onSelect={mode === "range" ? handleRangeSelect : onChange}
              onMonthChange={setMonth}
              {...props}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
